import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlidersHorizontal, MapPin, ChevronDown, X } from 'lucide-react';
import { Hero } from '../components/Hero';
import { LatestAds } from '../components/LatestAds';
import { CategoryAds } from '../components/CategoryAds';
import { useGetCategoryTreeQuery, useGetCitiesQuery } from '@nimbazaar/store';

export function Home() {
  const navigate = useNavigate();
  const { data } = useGetCategoryTreeQuery();
  const { data: citiesData } = useGetCitiesQuery();
  const categories = data?.data ?? [];
  const cities = citiesData?.data ?? [];

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const cityBtnRef = useRef<HTMLButtonElement>(null);

  // Show first 4 root categories dynamically
  const topCategories = categories.slice(0, 4);

  const hasFilters = selectedCategory || selectedCity;

  return (
    <>
      <Hero />

      {/* Filter Bar */}
      <div className="bg-white border-b border-gray-100 sticky top-[60px] z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">
            {/* Filter icon */}
            <div className="flex items-center gap-1.5 text-gray-500 shrink-0">
              <SlidersHorizontal className="w-4 h-4" />
              <span className="text-xs font-medium hidden sm:inline">Filters</span>
            </div>

            <div className="w-px h-6 bg-gray-200 shrink-0" />

            {/* Category pills */}
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors shrink-0 ${
                !selectedCategory
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(selectedCategory === cat.id ? '' : cat.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors shrink-0 ${
                  selectedCategory === cat.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat.name}
              </button>
            ))}

            <div className="w-px h-6 bg-gray-200 shrink-0" />

            {/* City filter */}
            <div className="shrink-0">
              <button
                ref={cityBtnRef}
                onClick={() => setShowCityDropdown(!showCityDropdown)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  selectedCity
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <MapPin className="w-3 h-3" />
                {selectedCity || 'All Cities'}
                <ChevronDown className="w-3 h-3" />
              </button>
            </div>

            {showCityDropdown && (
              <>
                <div className="fixed inset-0 z-[60]" onClick={() => setShowCityDropdown(false)} />
                <div
                  className="fixed z-[70] bg-white rounded-xl shadow-xl border border-gray-200 py-1 w-48 max-h-64 overflow-y-auto"
                  style={{
                    top: (cityBtnRef.current?.getBoundingClientRect().bottom ?? 0) + 4,
                    left: cityBtnRef.current?.getBoundingClientRect().left ?? 0,
                  }}
                >
                  <button
                    onClick={() => { setSelectedCity(''); setShowCityDropdown(false); }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${!selectedCity ? 'text-primary-600 font-medium' : 'text-gray-700'}`}
                  >
                    All Cities
                  </button>
                  {cities.map((city) => (
                    <button
                      key={city.id}
                      onClick={() => { setSelectedCity(city.name); setShowCityDropdown(false); }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${selectedCity === city.name ? 'text-primary-600 font-medium' : 'text-gray-700'}`}
                    >
                      {city.name}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Clear filters */}
            {hasFilters && (
              <button
                onClick={() => { setSelectedCategory(''); setSelectedCity(''); }}
                className="flex items-center gap-1 px-2 py-1.5 text-xs text-red-500 hover:text-red-600 shrink-0"
              >
                <X className="w-3 h-3" />
                Clear
              </button>
            )}

            {/* View all link */}
            <button
              onClick={() => navigate(`/ads${selectedCategory ? `?categoryId=${selectedCategory}` : ''}${selectedCity ? `${selectedCategory ? '&' : '?'}location=${selectedCity}` : ''}`)}
              className="ml-auto text-xs text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap shrink-0"
            >
              View All Ads →
            </button>
          </div>
        </div>
      </div>

      <LatestAds categoryId={selectedCategory} location={selectedCity} />
      {!hasFilters && topCategories.map((cat) => (
        <CategoryAds
          key={cat.id}
          title={cat.name}
          categoryId={cat.id}
          categorySlug={cat.slug}
        />
      ))}
    </>
  );
}
